import { ContentDelivery } from '@episerver/spa-core';
import { useRef } from 'react';
import { LinksListBlock } from './LinkListBlock';
import Link from '../../../../Shared/Link/link';
import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon, Icon, InlineButton } from '@norges-domstoler/dds-components';

type MenuItemLinkModel = {
    links: ContentDelivery.LinkListProperty;
    linksHeading: string;
};

type MenuItemProps = {
    childPageVisited: boolean;
    menuItemHeading: string;
    clickOnMenuItem?: () => void;
    clickedOnLink?: () => void;
    href?: string;
    id?: string;
    menuItemList?: MenuItemLinkModel[];
    selected?: boolean;
    isMobile?: boolean;
};

export const MenuItem = ({
    childPageVisited,
    menuItemHeading,
    clickOnMenuItem,
    clickedOnLink,
    href,
    id,
    isMobile = false,
    menuItemList = [],
    selected = false,
    ...props
}: MenuItemProps) => {
    const links = menuItemList
        ?.map((menuItemModel, index) => {
            const { linksHeading, links: linkList } = menuItemModel;
            if (!linksHeading || !linkList?.value?.length) {
                return null;
            }

            return (
                <LinksListBlock
                    key={`${index}-${linksHeading}`}
                    header={linksHeading}
                    listProp={linkList}
                    isMobile={isMobile}
                    isMenuOpen={clickedOnLink}
                    {...props}
                    className="sub-menu-column"
                />
            );
        })
        .filter(Boolean);

    if (!href && !menuItemHeading && links.length === 0) {
        return null;
    }

    const className = `header-menu-item menu-link-button-hover ${selected ? 'selected' : ''} ${
        childPageVisited ? 'child-page-visited' : ''
    }`;

    const panelId = id ? `${id}-panel` : undefined;

    const liRef = useRef<HTMLLIElement>(null);

    const closeMenuOnBlur = (event: React.FocusEvent<HTMLLIElement>) => {
        if (isMobile) return;

        if (!liRef.current?.contains(event.relatedTarget as Node | null)) {
            clickedOnLink?.();
        }
    };

    const closeMenuOnEscape = (event: React.KeyboardEvent<HTMLLIElement>) => {
        if (event.key === 'Escape' && selected) {
            clickedOnLink?.();
            const button = id ? document.getElementById(id) : null;
            button?.focus();
        }
    };

    return (
        <li
            role="none"
            ref={liRef}
            onBlur={!href ? closeMenuOnBlur : undefined}
            onKeyDown={!href ? closeMenuOnEscape : undefined}
        >
            {href ? (
                <Link role="menuitem" onClick={clickedOnLink} href={href} id={id} className={className}>
                    <span>{menuItemHeading}</span>
                    <Icon
                        className="menu-item-button-mobile-icon"
                        color="rgba(53, 71, 84, 1)"
                        icon={ArrowRightIcon}
                        iconSize="small"
                    />
                </Link>
            ) : (
                <InlineButton
                    role="menuitem"
                    aria-haspopup="menu"
                    aria-expanded={selected}
                    aria-controls={panelId}
                    id={id}
                    className={className}
                    onClick={clickOnMenuItem}
                >
                    <span>{menuItemHeading}</span>
                    <Icon
                        color="rgba(53, 71, 84, 1)"
                        icon={selected ? ChevronUpIcon : ChevronDownIcon}
                        iconSize="small"
                        className="menu-item-button-mobile-icon"
                    />
                </InlineButton>
            )}
            {!href && (
                <div id={panelId}>
                    <div className={`nav-menu-group ${selected ? 'selected' : ''}`}>{links}</div>
                </div>
            )}
        </li>
    );
};

export default MenuItem;
